import React, { useState, useEffect, useRef } from 'react';
import ProfileSignature from '../../components/ProfileSignature/ProfileSignature';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { useLanguage } from '../../i18n/LanguageContext';
import './Profile.css';
import { phoneInputProps, PhoneHint } from '../../utils/phone';

const Profile = () => {
    const { updateUser, user } = useAuth();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [memberProfile, setMemberProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [profileMissing, setProfileMissing] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [activeTab, setActiveTab] = useState('personal'); // Tab state
    const [branches, setBranches] = useState([]);
    const [validationError, setValidationError] = useState('');
    const [formData, setFormData] = useState({
        first_name: '',
        middle_name: '',
        last_name: '',
        email: '',
        phone: '',
        gender: '',
        date_of_birth: '',
        national_id: '',
        address: '',
        emergency_contact: '',
        emergency_phone: '',
        employer: '',
        employment_number: '',
        job_title: '',
        department: '',
        branch_id: ''
    });
    const fileInputRef = useRef(null);

    // Check if user is admin - redirect to admin profile
    useEffect(() => {
        if (user && user.role !== 'member') {
            navigate('/staff-landing', { replace: true });
        }
    }, [user, navigate]);

    useEffect(() => {
        if (!user || user.role !== 'member') return;
        loadProfile();
        api.getBranchOptions().then(response => setBranches(response?.data || [])).catch(error => console.error('Failed to load branches:', error));
    }, [user]);

    const loadProfile = async () => {
        try {
            setLoading(true);
            const response = await api.getMemberProfile();
            // API returns {status, message, data: {member, contributions, ...}}
            const memberData = response?.data?.member || response?.data || response;
            if (memberData) {
                setMemberProfile(memberData);
                if (memberData.membership_status !== 'active') setEditMode(true);
                setFormData({
                    first_name: memberData.first_name || '',
                    middle_name: memberData.middle_name || '',
                    last_name: memberData.last_name || '',
                    // Imported members often have no email on their member record: start from their login email.
                    email: memberData.email || user?.email || '',
                    phone: memberData.phone || '',
                    gender: memberData.gender || '',
                    date_of_birth: memberData.date_of_birth || '',
                    national_id: memberData.national_id || '',
                    address: memberData.address || '',
                    emergency_contact: memberData.emergency_contact || '',
                    emergency_phone: memberData.emergency_phone || '',
                    employer: memberData.employer || '',
                    employment_number: memberData.employment_number || '',
                    job_title: memberData.job_title || '',
                    department: memberData.department || '',
                    branch_id: memberData.branch_id || ''
                });
            }
        } catch (error) {
            console.error('Failed to load profile:', error);
            if (String(error.message).includes('Member profile not found')) setProfileMissing(true);
            else alert(t('Hitilafu imetokea kupakia profile'));
        } finally {
            setLoading(false);
        }
    };

    const handlePhotoClick = () => {
        fileInputRef.current?.click();
    };

    const handlePhotoChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert(t('Tafadhali chagua picha sahihi (JPG, PNG, etc.)'));
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            alert(t('Picha ni kubwa sana. Ukubwa wa juu ni 5MB'));
            return;
        }

        try {
            setUploading(true);
            const response = await api.uploadProfilePicture(file);
            if (response && response.data) {
                // Use the URL directly from the response
                const photoUrl = response.data.url || response.data.photo;
                setMemberProfile(prev => ({ ...prev, photo: photoUrl }));
                // Update user context if needed
                updateUser({ photo: photoUrl });
                alert(t('Picha imebadilishwa!'));
                // Reload to get updated data
                await loadProfile();
            }
        } catch (error) {
            console.error('Failed to upload photo:', error);
            alert(error.message || t('Hitilafu imetokea kupakia picha'));
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const requiredFields = [
            ['first_name', 'First name', 'personal'], ['last_name', 'Last name', 'personal'],
            ['email', 'Email', 'personal'], ['gender', 'Gender', 'personal'],
            ['date_of_birth', 'Date of birth', 'personal'], ['national_id', 'NIDA/NIN', 'personal'],
            ['employment_number', 'Employment number', 'personal'], ['employer', 'Employer', 'employment'],
            ['branch_id', 'Branch', 'employment'], ['phone', 'Phone number', 'contact']
        ];
        const missing = requiredFields.filter(([, , tab]) => tab === activeTab).filter(([field]) => String(formData[field] ?? '').trim() === '');
        if (missing.length) {
            setValidationError(`Please complete this section: ${missing.map(([, label]) => label).join(', ')}.`);
            return;
        }
        
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (activeTab === 'personal' && !emailRegex.test(formData.email)) {
            setValidationError('Please enter a valid email address, for example name@example.com.');
            setActiveTab('personal');
            return;
        }
        setValidationError('');
        try {
            const response = await api.updateMemberProfile(formData);
            if (response && response.data) {
                setMemberProfile(response.data);
                updateUser({
                    membership_status: response.data.membership_status,
                    profile_completed: !!response.data.profile_completed_at,
                    member_number: response.data.member_number
                });
                if (response.missing_fields?.length) {
                    setMemberProfile(response.data);
                    setValidationError(`${activeTab === 'personal' ? 'Personal information' : activeTab === 'employment' ? 'Employment information' : 'Contact information'} saved. Complete the remaining tabs to finish your profile.`);
                    return;
                }
                setEditMode(false);
                alert(t('Taarifa zimebadilishwa!'));
                loadProfile(); // Reload to get updated data
            }
        } catch (error) {
            console.error('Failed to update profile:', error);
            alert(error.message || t('Hitilafu imetokea kubadilisha taarifa'));
        }
    };


    const getInitials = (name) => {
        if (!name) return 'U';
        const parts = name.split(' ');
        return parts.map(p => p[0]).join('').toUpperCase().slice(0, 2);
    };

    if (profileMissing) {
        return <div className="profile-page"><ProfileSignature /><div className="profile-section-card info-card"><h2>{t('Member profile not found')}</h2><p>{t('This user account is not linked to a membership record. Please contact the administrator.')}</p></div></div>;
    }

    if (loading || !memberProfile) {
        return (
            <div className="profile-page">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>{t('Inapakia...')}</p>
                </div>
            </div>
        );
    }
    
    // Wait for the profile to load before showing the form. (Imported members often have no email yet, so an empty
    // email must not block editing: it is one of the fields they are asked to complete.)
    if (editMode && !memberProfile) {
        return (
            <div className="profile-page">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>{t('Inapakia taarifa...')}</p>
                </div>
            </div>
        );
    }

    const profilePicUrl = memberProfile?.photo 
        ? (memberProfile.photo.startsWith('http') 
            ? memberProfile.photo 
            : `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080'}/uploads/members/${memberProfile.photo}`)
        : null;

    return (
        <div className="profile-page">
            <ProfileSignature />
            <div className="profile-header">
                <h1 className="page-title">{t('Wasifu Wangu')}</h1>
                <p className="page-subtitle">{t('Angalia na hariri taarifa zako za kibinafsi')}</p>
            </div>

            {/* Large Profile Picture Section at Top */}
            <div className="profile-photo-banner">
                <div className="photo-banner-content">
                    <div className="photo-preview-large">
                        {profilePicUrl ? (
                            <img src={profilePicUrl} alt="Profile" className="profile-photo-large" />
                        ) : (
                            <div className="profile-photo-placeholder-large">
                                {getInitials(memberProfile?.first_name + ' ' + memberProfile?.last_name)}
                            </div>
                        )}
                        {uploading && (
                            <div className="photo-uploading-overlay">
                                <div className="spinner"></div>
                                <p>{t('Inapakia picha...')}</p>
                            </div>
                        )}
                    </div>
                    <div className="photo-info">
                        <h2 className="photo-title">
                            {[memberProfile?.first_name, memberProfile?.middle_name, memberProfile?.last_name]
                                .filter(Boolean)
                                .join(' ') || t('Mwanachama')}
                        </h2>
                        <p className="photo-member-number">{t('Namba:')} {memberProfile?.member_number || '-'}</p>
                        <div className="photo-upload-section">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handlePhotoChange}
                                style={{ display: 'none' }}
                            />
                            <button
                                className="btn btn-primary btn-large"
                                onClick={handlePhotoClick}
                                disabled={uploading}
                            >
                                📸 {uploading ? t('Inapakia...') : memberProfile?.photo ? t('Badilisha Picha') : t('Weka Picha')}
                            </button>
                            <p className="photo-hint-large">{t('Chagua picha nzuri ya uso wako (JPG, PNG, au GIF, hadi 5MB)')}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="profile-content">
                {/* Profile Info Card with Tabs */}
                <div className="profile-section-card info-card">
                    <div className="card-header">
                        <h2 className="card-title">{t('Taarifa za Kibinafsi')}</h2>
                        {!editMode && (
                            <button
                                className="btn btn-secondary"
                                onClick={() => setEditMode(true)}
                            >
                                ✏️ {t('Hariri')}
                            </button>
                        )}
                    </div>

                    {editMode ? (
                        <div className="profile-tabs-container">
                            {/* Tab Navigation */}
                            <div className="profile-tabs-nav">
                                <button 
                                    type="button"
                                    className={`tab-button ${activeTab === 'personal' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('personal')}
                                >
                                    👤 {t('Personal Information')}
                                </button>
                                <button 
                                    type="button"
                                    className={`tab-button ${activeTab === 'employment' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('employment')}
                                >
                                    💼 {t('Employment Information')}
                                </button>
                                <button 
                                    type="button"
                                    className={`tab-button ${activeTab === 'contact' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('contact')}
                                >
                                    📞 {t('Contact Information')}
                                </button>
                            </div>

                            {/* Tab Content */}
                            <form onSubmit={handleSubmit} className="profile-form">
                                {validationError && <div className="profile-validation-message" role="alert">{validationError}</div>}
                                {/* Personal Information Tab */}
                                {activeTab === 'personal' && (
                                    <div className="tab-content">
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>{t('Jina la Kwanza')} *</label>
                                                <input
                                                    type="text"
                                                    value={formData.first_name}
                                                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                                    required
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>{t('Jina la Kati')}</label>
                                                <input
                                                    type="text"
                                                    value={formData.middle_name}
                                                    onChange={(e) => setFormData({ ...formData, middle_name: e.target.value })}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>{t('Jinsia')} *</label>
                                                <select value={formData.gender} onChange={(e) => setFormData({ ...formData, gender: e.target.value })} required>
                                                    <option value="">{t('Chagua jinsia')}</option>
                                                    <option value="male">{t('Mwanaume')}</option>
                                                    <option value="female">{t('Mwanamke')}</option>
                                                    <option value="other">{t('Nyingine')}</option>
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label>{t('Tarehe ya Kuzaliwa')} *</label>
                                                <input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })} required />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label>{t('NIDA/NIN')} *</label>
                                            <input type="text" value={formData.national_id} onChange={(e) => setFormData({ ...formData, national_id: e.target.value })} required />
                                        </div>

                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>{t('Jina la Mwisho')} *</label>
                                                <input
                                                    type="text"
                                                    value={formData.last_name}
                                                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                                    required
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>{t('Barua pepe')} *</label>
                                                <input
                                                    type="email"
                                                    value={formData.email}
                                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label>{t('Namba ya Ajira')} *</label>
                                            <input
                                                type="text"
                                                value={formData.employment_number}
                                                onChange={(e) => setFormData({ ...formData, employment_number: e.target.value })}
                                                required
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Employment Information Tab */}
                                {activeTab === 'employment' && (
                                    <div className="tab-content">
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>{t('Mwajiri')} *</label>
                                                <input
                                                    type="text"
                                                    value={formData.employer}
                                                    onChange={(e) => setFormData({ ...formData, employer: e.target.value })}
                                                    required
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>{t('Cheo')}</label>
                                                <input
                                                    type="text"
                                                    value={formData.job_title}
                                                    onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                                                />
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label>{t('Idara')}</label>
                                            <input
                                                type="text"
                                                value={formData.department}
                                                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>{t('Tawi')} *</label>
                                            <select value={formData.branch_id} onChange={(e) => setFormData({ ...formData, branch_id: e.target.value })} required>
                                                <option value="">{t('Chagua tawi')}</option>
                                                {branches.map(branch => <option key={branch.id} value={branch.id}>{branch.code} — {branch.name}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                )}

                                {/* Contact Information Tab */}
                                {activeTab === 'contact' && (
                                    <div className="tab-content">
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>{t('Simu')} *</label>
                                                <input
                                                    {...phoneInputProps}
                                                    value={formData.phone}
                                                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                                    required
                                                />
                                                <PhoneHint value={formData.phone} t={t} />
                                            </div>
                                            <div className="form-group">
                                                <label>{t('Anwani')}</label>
                                                <input
                                                    type="text"
                                                    value={formData.address}
                                                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                                />
                                            </div>
                                        </div>

                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>{t('Jina la Dharura')}</label>
                                                <input
                                                    type="text"
                                                    value={formData.emergency_contact}
                                                    onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>{t('Simu ya Dharura')}</label>
                                                <input
                                                    {...phoneInputProps}
                                                    value={formData.emergency_phone}
                                                    onChange={(e) => setFormData({ ...formData, emergency_phone: e.target.value })}
                                                />
                                                <PhoneHint value={formData.emergency_phone} t={t} />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="form-actions">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            setEditMode(false);
                                            setActiveTab('personal');
                                            setFormData({
                                                first_name: memberProfile.first_name || '',
                                                middle_name: memberProfile.middle_name || '',
                                                last_name: memberProfile.last_name || '',
                                                email: memberProfile.email || '',
                                                phone: memberProfile.phone || '',
                                                gender: memberProfile.gender || '',
                                                date_of_birth: memberProfile.date_of_birth || '',
                                                national_id: memberProfile.national_id || '',
                                                address: memberProfile.address || '',
                                                emergency_contact: memberProfile.emergency_contact || '',
                                                emergency_phone: memberProfile.emergency_phone || '',
                                                employer: memberProfile.employer || '',
                                                employment_number: memberProfile.employment_number || '',
                                                job_title: memberProfile.job_title || '',
                                                department: memberProfile.department || '',
                                                branch_id: memberProfile.branch_id || ''
                                            });
                                        }}
                                    >
                                        {t('Ghairi')}
                                    </button>
                                    <button type="submit" className="btn btn-primary">
                                        {activeTab === 'personal' && 'Save Personal Information'}
                                        {activeTab === 'employment' && 'Save Employment Information'}
                                        {activeTab === 'contact' && 'Save Contact Information'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    ) : (
                        <div className="profile-tabs-container">
                            {/* Tab Navigation */}
                            <div className="profile-tabs-nav">
                                <button 
                                    type="button"
                                    className={`tab-button ${activeTab === 'personal' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('personal')}
                                >
                                    👤 {t('Personal Information')}
                                </button>
                                <button 
                                    type="button"
                                    className={`tab-button ${activeTab === 'employment' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('employment')}
                                >
                                    💼 {t('Employment Information')}
                                </button>
                                <button 
                                    type="button"
                                    className={`tab-button ${activeTab === 'contact' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('contact')}
                                >
                                    📞 {t('Contact Information')}
                                </button>
                            </div>

                            <div className="profile-info">
                                {/* Personal Information Tab */}
                                {activeTab === 'personal' && (
                                    <div className="tab-content">
                                        <div className="info-row">
                                            <div className="info-item">
                                                <label>{t('Jina Kamili')}</label>
                                                <p>
                                                    {[memberProfile?.first_name, memberProfile?.middle_name, memberProfile?.last_name]
                                                        .filter(Boolean)
                                                        .join(' ') || '-'}
                                                </p>
                                            </div>
                                            <div className="info-item">
                                                <label>{t('Namba ya Mwanachama')}</label>
                                                <p>{memberProfile?.member_number || '-'}</p>
                                            </div>
                                        </div>

                                        <div className="info-row">
                                            <div className="info-item">
                                                <label>{t('Tarehe ya Ujumbe')}</label>
                                                <p>{memberProfile?.membership_date ? new Date(memberProfile.membership_date).toLocaleDateString('sw-TZ') : '-'}</p>
                                            </div>
                                            <div className="info-item">
                                                <label>{t('Hali ya Ujumbe')}</label>
                                                <p>
                                                    <span className={`status-badge status-${memberProfile?.membership_status}`}>
                                                        {memberProfile?.membership_status || '-'}
                                                    </span>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Employment Information Tab */}
                                {activeTab === 'employment' && (
                                    <div className="tab-content">
                                        <div className="info-row">
                                            <div className="info-item">
                                                <label>{t('Mwajiri')}</label>
                                                <p>{memberProfile?.employer || '-'}</p>
                                            </div>
                                            <div className="info-item">
                                                <label>{t('Cheo')}</label>
                                                <p>{memberProfile?.job_title || '-'}</p>
                                            </div>
                                        </div>

                                        <div className="info-row">
                                            <div className="info-item">
                                                <label>{t('Idara')}</label>
                                                <p>{memberProfile?.department || '-'}</p>
                                            </div>
                                            <div className="info-item">
                                                <label>{t('Namba ya Ajira')}</label>
                                                <p>{memberProfile?.employment_number || '-'}</p>
                                            </div>
                                        </div>
                                        <div className="info-row">
                                            <div className="info-item">
                                                <label>{t('Tawi')}</label>
                                                <p>{memberProfile?.branch_name || '-'}</p>
                                            </div>
                                        </div>

                                        <div className="info-row">
                                            <div className="info-item">
                                                <label>{t('Pembezo la Mshahara')}</label>
                                                <p>TZS {memberProfile?.salary_range || '0'}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Contact Information Tab */}
                                {activeTab === 'contact' && (
                                    <div className="tab-content">
                                        <div className="info-row">
                                            <div className="info-item">
                                                <label>{t('Barua pepe')}</label>
                                                <p>{memberProfile?.email || '-'}</p>
                                            </div>
                                            <div className="info-item">
                                                <label>{t('Simu')}</label>
                                                <p>{memberProfile?.phone || '-'}</p>
                                            </div>
                                        </div>

                                        <div className="info-item full-width">
                                            <label>{t('Anwani')}</label>
                                            <p>{memberProfile?.address || '-'}</p>
                                        </div>

                                        <div className="info-row">
                                            <div className="info-item">
                                                <label>{t('Jina la Dharura')}</label>
                                                <p>{memberProfile?.emergency_contact || '-'}</p>
                                            </div>
                                            <div className="info-item">
                                                <label>{t('Simu ya Dharura')}</label>
                                                <p>{memberProfile?.emergency_phone || '-'}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>


            </div>
        </div>
    );
};

export default Profile;
